import { useState, useEffect } from 'react'

import { DeliveryEligibility, BannerLocation, BannerConfig } from '../types'
import { getCurrentTimeSection } from '../utils/timeSegments'
import {
  getDeliveryEligibility,
  calculateBannerConfig,
  getDeliveryEligibilityByDate,
} from '../utils/nddEligibility'
import { calculateCountdown } from '../utils/dateUtils'
import { useIsNDDBannersEnabled } from './useIsNDDBannersEnabled'

/**
 * Hook for NDD banner system with real-time eligibility and location-specific messages.
 *
 * Usage:
 * - Time-based: `const { eligibility, isNDD, bannerConfig, timeLeft } = useDeliveryEligibility('checkout')`
 * - Date-based: `const { eligibility, isNDD, bannerConfig, timeLeft } = useDeliveryEligibility('checkout', '2024-12-26')`
 *
 * Locations: 'homepage' | 'delivery-days' | 'box-summary' | 'checkout'
 *
 * Configuration:
 * - Eligibility rules: config/eligibilityConfig.ts (time sections → eligibility types)
 * - Banner content: config/bannerConfig.ts (location/eligibility → messages)
 *
 * Features:
 * - Time-based: Updates eligibility every minute based on current time
 * - Date-based: Checks if delivery date is tomorrow (NDD) or later (2DD+), updates countdown every minute
 * - Lazy evaluation: only calculates {deliveryDay}/{countdown} when templates use them
 * - Prevents unnecessary re-renders using deep comparison of banner content
 * - Returns null bannerConfig when feature disabled or no content configured
 * - Auto-updates: Both time-based and date-based modes update every minute for countdown accuracy
 * - timeLeft: Returns minutes remaining until cutoff (null for non-countdown eligibility types)
 *
 * Placeholders automatically replaced:
 * - {deliveryDay}: "Sunday", "Monday", etc. (or "unknown" for 3DD+)
 * - {countdown}: "2 hrs 30 min" format for NDD cutoff timing
 */

interface UseDeliveryEligibilityResult {
  eligibility: DeliveryEligibility | null
  isNDD: boolean
  isEnabled: boolean
  bannerConfig: BannerConfig | null
  timeLeft: number | null // minutes remaining until cutoff
}

/**
 * Sets up a 60-second interval with the provided callback function
 * Returns a cleanup function to clear the interval
 *
 * @param callback - Function to call every 60 seconds
 * @returns Cleanup function to clear the interval
 */
function setupMinuteInterval(callback: () => void): () => void {
  const interval = setInterval(callback, 60000)
  return () => clearInterval(interval)
}

/**
 * Deep compare two banner configurations to check if they're equivalent
 * Prevents unnecessary re-renders when content hasn't actually changed
 *
 * @param a - First banner config
 * @param b - Second banner config
 * @returns True if the configs have the same content
 */
function isEqualBannerConfig(a: BannerConfig | null, b: BannerConfig | null): boolean {
  if (a === b) return true
  if (a === null || b === null) return false

  // Simple deep comparison using JSON.stringify
  // This works well for banner configs since they're simple objects
  return JSON.stringify(a) === JSON.stringify(b)
}

/**
 * Hook to get delivery eligibility based on time or specific delivery date
 * Only runs if NDD banners feature is enabled
 *
 * @param location - Banner location to get specific messages
 * @param deliveryDate - Optional delivery date to check. If not provided, uses time-based eligibility
 * @returns Object with eligibility, feature flag status, banner config, and timeLeft in minutes
 */
export const useDeliveryEligibility = (
  location: BannerLocation,
  deliveryDate?: Date | string,
): UseDeliveryEligibilityResult => {
  const isFeatureEnabled = useIsNDDBannersEnabled()
  const [eligibility, setEligibility] = useState<DeliveryEligibility | null>(null)
  const [bannerConfig, setBannerConfig] = useState<BannerConfig | null>(null)
  const [timeLeft, setTimeLeft] = useState<number | null>(null)
  const [currentTime, setCurrentTime] = useState(Date.now())

  // Unified eligibility logic
  useEffect(() => {
    if (!isFeatureEnabled) {
      setEligibility(null)
      return
    }

    // Date-based logic: calculate NDD, 2DD, or 3DD+
    if (deliveryDate) {
      const eligibilityType = getDeliveryEligibilityByDate(deliveryDate)
      setEligibility(eligibilityType)

      // Update current time every minute to trigger countdown recalculation
      const updateTime = () => {
        setCurrentTime(Date.now())
      }

      // Set up interval to update countdown display
      return setupMinuteInterval(updateTime)
    }

    // Time-based logic
    const updateEligibility = () => {
      const timeSection = getCurrentTimeSection()

      const currentEligibility = getDeliveryEligibility(timeSection)

      setEligibility(currentEligibility)
      setCurrentTime(Date.now()) // Update current time to trigger banner recalculation
    }

    // Set initial eligibility
    updateEligibility()

    // Update every minute to stay in sync with time changes
    return setupMinuteInterval(updateEligibility)
  }, [isFeatureEnabled, deliveryDate])

  // Banner config calculation - includes currentTime to trigger recalculation for countdown
  useEffect(() => {
    if (!isFeatureEnabled || !eligibility) {
      setBannerConfig(null)
      return
    }

    const newBannerConfig = calculateBannerConfig(location, eligibility)

    // Only update if content actually changed to prevent unnecessary re-renders
    setBannerConfig((prevBannerConfig) => {
      return isEqualBannerConfig(prevBannerConfig, newBannerConfig)
        ? prevBannerConfig // Keep same reference if content unchanged
        : newBannerConfig // Update only if content actually changed
    })
  }, [isFeatureEnabled, location, eligibility, currentTime])

  // TimeLeft calculation - minutes remaining until cutoff
  useEffect(() => {
    if (!isFeatureEnabled || !eligibility) {
      setTimeLeft(null)
      return
    }

    const countdownData = calculateCountdown(eligibility)
    setTimeLeft(countdownData?.totalMinutes || null)
  }, [isFeatureEnabled, eligibility, currentTime])

  return {
    eligibility,
    isNDD: eligibility === 'NDD',
    isEnabled: isFeatureEnabled,
    bannerConfig,
    timeLeft,
  }
}
